/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState } from 'react';
import clsx from 'clsx';

import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import CustomIcons from '../icons/CustomIcons';
import Zoom from '@material-ui/core/Zoom';
import DarkTooltip from './DarkTooltip';

const useStyles = makeStyles((theme) => ({
    apiEndpoint: {
        borderRadius: theme.spacing(3),
        maxWidth: theme.spacing(50),
        height: theme.spacing(6),
        ...theme.custom.elements.copyTextField.textFieldStyle
    },
    notchedOutline: {
        ...theme.custom.elements.copyTextField.borderStyle
    },
    copyIconButton: {
        height: theme.spacing(6),
        width: theme.spacing(6),
        zIndex:1,
        ...theme.custom.elements.copyTextField.copyIconButtonStyle
    },
}));

interface CopyTextFieldProps {
  textContent: string;
  disabled?: boolean;
  className?: string;
  testId?: string;
}

function CopyTextField(props: CopyTextFieldProps) {
  const classes = useStyles();
  const [copiedTooltipVisible, setCopiedTooltipVisible] = useState(false);
  const textContent: string = props.textContent;
  const disabled: boolean | undefined = props.disabled;
  const className = props.className;
  const testId: string | undefined = props?.testId;

  const handleCopyClick = (event: any) => {
    navigator.clipboard.writeText(textContent);
    setCopiedTooltipVisible(true);
    setTimeout(() => {
      setCopiedTooltipVisible(false);
    }, 2000);
  };

  return (
    <TextField
      id={testId ? testId : 'copy-textfield'}
      variant='outlined'
      InputProps={{
        endAdornment: (
          <DarkTooltip
            title={'Copied'}
            TransitionComponent={Zoom}
            placement='right'
            open={copiedTooltipVisible}
          >
            <IconButton
              onClick={handleCopyClick}
              className={classes.copyIconButton}
            >
              <CustomIcons icon={'copy-icon'} />
            </IconButton>
          </DarkTooltip>
        ),
        classes: {
          notchedOutline: classes.notchedOutline,
          root: clsx(classes.apiEndpoint, className),
        },
      }}
      value={textContent}
      fullWidth
      disabled={disabled}
      data-testid={testId}
    />
  );
}

export default CopyTextField;
